import * as Yup from 'yup';
import { isNil, isNumber } from 'lodash';

import {
  CustomerMspOrVmsOverviewDto,
  OvertimeEnum,
} from 'api/backofficeApiClient';
import {
  FinancialSchedulerFormValues,
  MultiRateFormScheduleValues,
} from 'components/modals/FinancialInfoScheduleModal';
import { FinancialInfoCalculator } from './calculationUtils';
import { FinancialInfoFormValues } from './FinancialInfo';

const requiredMessage = 'Field is required';
const maxCharactersLength = 255;

const getMultiRateInitialValidationSchema = (
  isViewDTBillRateDtPayRateFields: boolean,
  isTeammate?: boolean,
) => {
  const schema: any = {
    vmsRate: Yup.number()
      .nullable()
      .min(0, 'VMS Rate should be from 0 to 100')
      .max(100, 'VMS Rate should be from 0 to 100'),
    mspRate: Yup.number()
      .nullable()
      .min(0, 'MSP Rate should be from 0 to 100')
      .max(100, 'MSP Rate should be from 0 to 100'),
    billRate: Yup.number()
      .nullable()
      .min(0, 'Bill rate should be from 0 to 999999')
      .max(999999, 'Bill rate should be from 0 to 999999'),
    payRate: Yup.number()
      .nullable()
      .min(0, 'Pay rate should be from 0 to 999999')
      .max(999999, 'Pay rate should be from 0 to 999999')
      .when('billRate', {
        is: (billRate: number | null) => isNumber(billRate),
        then: Yup.number()
          .nullable()
          .max(Yup.ref('billRate'), 'Pay rate should be less than bill rate'),
      }),
    overtimeBillRate: Yup.number()
      .nullable()
      .min(0, 'OT bill rate should be from 0 to 999999')
      .max(999999, 'OT bill rate should be from 0 to 999999'),
    overtimePayRate: Yup.number()
      .nullable()
      .min(0, 'OT pay rate should be from 0 to 999999')
      .when('overtime', {
        is: (overtime: OvertimeEnum | null) =>
          overtime === OvertimeEnum.Nonexempt,
        then: Yup.number()
          .nullable()
          .max(999999, 'OT pay rate should be from 0 to 999999'),
      }),
    profitRate: Yup.number().nullable(),
    overtime: Yup.number().nullable(),
    multiRates: Yup.array().of(
      Yup.object(initialMultipleRatesValidationSchema),
    ),
  };

  if (isViewDTBillRateDtPayRateFields) {
    schema.dtBillRate = Yup.number()
      .nullable()
      .min(0, 'DT Bill rate should be from 0 to 999999')
      .max(999999, 'DT Bill rate should be from 0 to 999999');
    schema.dtPayRate = Yup.number().nullable();
  }

  if (!isTeammate) {
    schema.profitRate = Yup.number()
      .nullable()
      .min(0, 'Profit rate should be from 0 to 999999')
      .max(999999, 'Profit rate should be from 0 to 999999');
  }

  return schema;
};

const getSubmitValidationSchema = (
  isW2Assignment: boolean,
  isViewDTBillRateDtPayRateFields: boolean,
  predefinedFinancialSchedulerValues?: FinancialSchedulerFormValues,
  isTeammate?: boolean,
  mspOrVmsOverviewInfo?: CustomerMspOrVmsOverviewDto,
) => {
  const initialValues = getMultiRateInitialValidationSchema(
    isViewDTBillRateDtPayRateFields,
    isTeammate,
  );
  const schema: any = {
    ...initialValues,
    vmsRate: initialValues.vmsRate.required('Field is required'),
    mspRate: initialValues.mspRate.required('Field is required'),
    billRate: initialValues.billRate
      .required('Field is required')
      .test(
        'minimum-bill-rate',
        function (
          this: Yup.TestContext<FinancialInfoFormValues>,
          value: number,
        ) {
          if (!isTeammate) return true;

          const { mbpRate, payRate, vmsRate, mspRate } = this.parent;
          const minBillRate =
            FinancialInfoCalculator.getMinimumBillRateForZeroProfit(
              { mbpRate, payRate },
              { vmsRate, mspRate },
            );

          if (value < minBillRate) {
            return this.createError({
              message: `A minimum bill rate of ${minBillRate.toFixed(
                2,
              )} is required`,
              path: this.path,
            });
          }

          return true;
        },
      ),
    payRate: initialValues.payRate.required('Field is required'),
    overtimeBillRate: Yup.lazy(() => {
      let schema: any = initialValues.overtimeBillRate;

      if (
        !predefinedFinancialSchedulerValues?.financialInfoScheduler?.hasOwnProperty(
          'overtimeBillRate',
        )
      ) {
        schema = initialValues.overtimeBillRate.when('overtime', {
          is: (overtime: OvertimeEnum | null) =>
            overtime !== OvertimeEnum.Exempt,
          then: initialValues.overtimeBillRate.required('Field is required'),
        });
      }

      return schema;
    }),
    overtimePayRate: initialValues.overtimePayRate.when('overtime', {
      is: (overtime: OvertimeEnum | null) => overtime !== OvertimeEnum.Exempt,
      then: initialValues.overtimePayRate.required('Field is required'),
    }),
    overtime: initialValues.overtime.required('Field is required'),
    multiRates: Yup.array().of(
      submitMultiRateValidationSchema(
        predefinedFinancialSchedulerValues?.multiRates,
      ),
    ),
  };

  if (isViewDTBillRateDtPayRateFields) {
    schema.dtBillRate = initialValues.dtBillRate.required('Field is required');
  }
  if (isW2Assignment) {
    schema.payType = Yup.number().nullable().required('Field is required');
  }

  return Yup.object(schema);
};

const initialPayCodeValidationSchema = {
  payCodeId: Yup.string(),
  payRate: Yup.number()
    .nullable()
    .min(0, 'Pay rate should be from 0 to 999999')
    .max(999999, 'Pay rate should be from 0 to 999999'),
  billRate: Yup.number()
    .nullable()
    .min(0, 'Bill rate should be from 0 to 999999')
    .max(999999, 'Bill rate should be from 0 to 999999'),
  specialInstructions: Yup.string()
    .trim()
    .max(
      maxCharactersLength,
      `Special instructions should be ${maxCharactersLength} characters maximum`,
    ),
  memberBilledIndicator: Yup.boolean().nullable(),
};

const initialMultipleRatesValidationSchema = {
  activePayCodeId: Yup.string(),
  payTypeId: Yup.string().nullable().required(requiredMessage),
  payCodesInfo: Yup.array().of(Yup.object(initialPayCodeValidationSchema)),
};

const submitPayCodeValidationSchema = () => {
  return Yup.object().shape({
    ...initialPayCodeValidationSchema,
    memberBilledIndicator:
      initialPayCodeValidationSchema.memberBilledIndicator.when(
        ['billRate', 'payRate'],
        {
          is: (billRate: number, payRate: number) =>
            !isNil(billRate) || !isNil(payRate),
          then: initialPayCodeValidationSchema.memberBilledIndicator.required(
            requiredMessage,
          ),
        },
      ),
    payRate: initialPayCodeValidationSchema.payRate.when('billRate', {
      is: (billRate: number) => !isNil(billRate),
      then: initialPayCodeValidationSchema.payRate.required(requiredMessage),
    }),
    billRate: initialPayCodeValidationSchema.billRate.test(
      'conditional-validation',
      '',
      function (value) {
        const { payRate, memberBilledIndicator } = this.parent;

        if (!isNil(payRate) && isNil(value)) {
          return this.createError({ message: requiredMessage });
        }

        if (
          !isNil(payRate) &&
          !memberBilledIndicator &&
          (isNil(value) || value <= 0.0)
        ) {
          return this.createError({
            message: 'The bill rate should be greater than $0.00.',
          });
        }

        return true;
      },
    ),
  });
};

const submitMultiRateValidationSchema = (
  predefinedMultiRatesScheduleValues?: MultiRateFormScheduleValues[],
) => {
  return Yup.object().shape({
    ...initialMultipleRatesValidationSchema,
    payCodesInfo: Yup.array()
      .of(submitPayCodeValidationSchema())
      .when('payTypeId', {
        is: (payTypeId: string) => {
          const matchingRateSchedule = predefinedMultiRatesScheduleValues?.find(
            (multiRate) => multiRate.payTypeId === payTypeId,
          );

          return !Boolean(matchingRateSchedule);
        },
        then: Yup.array().test(
          'at-least-one-full',
          'At least one pay code should be filled in',
          (array) => {
            return (array || []).some(
              (obj) => !isNil(obj.payRate) && !isNil(obj.billRate),
            );
          },
        ),
      }),
  });
};

export { getSubmitValidationSchema };
